import { useMemo } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import styled from "styled-components/macro";
import type { ChipType } from "../../../../../components/Chips/Chips";
import { FilterBy } from "../../../../../components/FilterBy/FilterBy";
import type { ApprovalsFiltersResponse } from "../../../../../types/types.Workflow";
import { UNASSIGNED_FILTER_ID } from "../../../../../util/collectionSearchUtils";
import { approvalStatusLabel } from "../utils";
import { mergeSelectedIntoOptions } from "./approvalsList.types";
import type { ApprovalsListFiltersState } from "./approvalsList.types";

const FilterPortalContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
  z-index: 900;
`;

type ApprovalsListFiltersProps = {
  filters: ApprovalsListFiltersState;
  onFiltersChange: (filters: ApprovalsListFiltersState) => void;
  /** Options from GET .../approvals/filters */
  apiFilters?: ApprovalsFiltersResponse;
  /**
   * list_details from the list response — used while /filters is still loading.
   */
  listDetails?: ApprovalsFiltersResponse["list_details"];
};

function toNamedChips(
  items: { id: string; name: string }[] | undefined
): ChipType[] {
  return (items ?? []).map((item) => ({
    id: item.id,
    name: item.name,
    type: "user",
  }));
}

export function ApprovalsListFilters({
  filters,
  onFiltersChange,
  apiFilters,
  listDetails,
}: ApprovalsListFiltersProps) {
  const { t } = useTranslation();

  const statusOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        (apiFilters?.status ?? []).map((status) => ({
          id: status,
          name: approvalStatusLabel(status, t),
        })),
        filters.status
      ),
    [apiFilters?.status, filters.status, t]
  );

  const assigneeOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        [
          { name: t("Unassigned"), id: UNASSIGNED_FILTER_ID },
          ...toNamedChips(apiFilters?.assignees),
        ],
        filters.assignee
      ),
    [apiFilters?.assignees, filters.assignee, t]
  );

  const approverOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        toNamedChips(apiFilters?.approvers),
        filters.approver
      ),
    [apiFilters?.approvers, filters.approver]
  );

  const stageOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        (apiFilters?.stages ?? []).map((stage) => ({
          id: stage.id,
          name: stage.name,
        })),
        filters.stage
      ),
    [apiFilters?.stages, filters.stage]
  );

  const requestedByOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        toNamedChips(apiFilters?.requested_by),
        filters.requested_by
      ),
    [apiFilters?.requested_by, filters.requested_by]
  );

  const approvalTypeOptions = useMemo(
    () =>
      mergeSelectedIntoOptions(
        (apiFilters?.approval_types ?? []).map((type) => ({
          id: type,
          name: type,
        })),
        filters.approval_type
      ),
    [apiFilters?.approval_types, filters.approval_type]
  );

  const updateFilter = (
    key: Exclude<keyof ApprovalsListFiltersState, "item_details">,
    values: ChipType[]
  ) => {
    onFiltersChange({ ...filters, [key]: values });
  };

  // Prefer /filters list_details; fall back to list response while facets load.
  const attributeFilters = (
    apiFilters?.list_details ??
    listDetails ??
    []
  ).filter((detail) => detail.show_as_filter && detail.key);

  return (
    <>
      {!document.getElementById("filter-dropdown-portal") &&
        ReactDOM.createPortal(
          <FilterPortalContainer id="filter-dropdown-portal" />,
          document.body
        )}
      {attributeFilters.map((detail) => {
        const key = detail.key;
        const selected = filters.item_details?.[key] ?? [];
        const options = mergeSelectedIntoOptions(
          toNamedChips(apiFilters?.item_detail_options?.[key]),
          selected
        );
        return (
          <FilterBy
            key={key}
            size="small"
            usePortal
            filter={`item_detail_${key}`}
            header={t(detail.heading || key)}
            label={t(detail.heading || key)}
            options={options}
            preselectedFilters={selected}
            setter={({ values }) =>
              onFiltersChange({
                ...filters,
                item_details: {
                  ...filters.item_details,
                  [key]: values,
                },
              })
            }
          />
        );
      })}
      <FilterBy
        size="small"
        usePortal
        filter="assignee"
        header={t("Assignee")}
        label={t("Assignee")}
        options={assigneeOptions}
        preselectedFilters={filters.assignee}
        setter={({ values }) => updateFilter("assignee", values)}
      />
      <FilterBy
        size="small"
        usePortal
        filter="approver"
        header={t("Approver")}
        label={t("Approver")}
        options={approverOptions}
        preselectedFilters={filters.approver}
        setter={({ values }) => updateFilter("approver", values)}
      />
      <FilterBy
        size="small"
        usePortal
        filter="status"
        header={t("Status")}
        label={t("Status")}
        options={statusOptions}
        preselectedFilters={filters.status}
        setter={({ values }) => updateFilter("status", values)}
      />
      <FilterBy
        size="small"
        usePortal
        filter="stage"
        header={t("Stage")}
        label={t("Stage")}
        options={stageOptions}
        preselectedFilters={filters.stage}
        setter={({ values }) => updateFilter("stage", values)}
      />
      <FilterBy
        size="small"
        usePortal
        filter="requested_by"
        header={t("Requested By")}
        label={t("Requested By")}
        options={requestedByOptions}
        preselectedFilters={filters.requested_by}
        setter={({ values }) => updateFilter("requested_by", values)}
      />
      <FilterBy
        size="small"
        usePortal
        filter="approval_type"
        header={t("Approval Type")}
        label={t("Approval Type")}
        options={approvalTypeOptions}
        preselectedFilters={filters.approval_type}
        setter={({ values }) => updateFilter("approval_type", values)}
      />
    </>
  );
}
